// Metric and imperial — the DISPLAY layer over numbers that are always metric.
//
// Giraffe's own numbers are SI, whatever the project's setting says: `area` and
// `grossArea` are m², and `perimeter`, `_height` and `floorToFloor` are metres.
// The project carries what the user READS,
// on `project.properties.units` (see giraffe/units.ts), and until now this app
// ignored it — so a project set to feet showed 21,153 for a gross area and the
// number meant m² while every other panel in Giraffe said ft².
//
// So: the model stays metric end to end, and this module is the one place the
// numbers are respelled on their way to a cell, and spelled back on their way
// to the project. Two rules make it safe:
//
//  1. Conversion is per FIELD, by dimension. An area is scaled by 10.76 and a
//     length by 3.28. Money rates — `salePrice`, `hardCost` and the rest — are
//     NOT converted: they are whatever the user typed against the project's own
//     units, so a $/ft² figure is already $/ft² and dividing it again would be
//     out by 10.76.
//  2. A field the app does not recognise is NOT converted, and wears no unit in
//     its header. Under-conversion shows the number this app has always shown;
//     over-conversion is a confidently mislabelled one. (Same call as the
//     measure classes in classify.ts: the quiet wrong number is the one to
//     design against.)

import type { CellValue, UnitSystem } from '@/model/types';
import type { DataRow, GiraffeData, SourceKind } from './types';
import { prettyField, tokenizeField } from './classify';

// Declared in model/types.ts, for the same reason MeasureClass is: the
// workbook's override of it is part of the saved document.
export type { UnitSystem };

/**
 * What a number MEASURES — orthogonal to its measure class.
 *
 * `floorToFloor` is a `rate` for aggregation (never summed) and a `length` for
 * units; `dwellingTotalCount` is a `total` and dimensionless. The two questions
 * are asked of the same field and neither answers the other.
 */
export type Dimension = 'area' | 'length' | 'none';

/** The international foot, not the survey foot. */
export const FT_PER_M = 3.280839895013123;
export const FT2_PER_M2 = FT_PER_M * FT_PER_M;

/** Areas the engine pre-computes, plus the names a site area travels under. */
const AREA_FIELDS = new Set([
  'area',
  'grossarea',
  'grossbuildingarea',
  'netarea',
  'saleablearea',
  'nettarea',
  'footprint',
  'sitearea',
  'plotarea',
  'landarea',
  'floorarea',
]);

/** Lengths. `_height` and `_baseElevation` are the engine's own bookkeeping. */
const LENGTH_FIELDS = new Set([
  'perimeter',
  'height',
  '_height',
  'floortofloor',
  'length',
  'width',
  'depth',
  'frontage',
  'setback',
  'elevation',
  '_baseelevation',
]);

/** Tokens that are unambiguous on their own — someone's own column heading. */
const AREA_TOKENS = new Set(['sqm', 'm2', 'sqft', 'sf', 'gfa', 'gia', 'nia', 'nsa', 'gba']);
const LENGTH_TOKENS = new Set(['perimeter', 'height', 'length', 'width', 'depth', 'frontage', 'setback']);

/**
 * What a field measures, from its name.
 *
 * Name-driven and nothing else — the VALUES cannot tell an area from a count,
 * and there is no unit on the wire. Recognised names first, then tokens, then
 * `none`.
 *
 * A dwelling-mix field is dimensionless by decree: the mix keys are whatever
 * the user typed, so `dwelling-1BED-AreaPer` and `dwelling-1BED-PricePer` are
 * indistinguishable to a token test, and a mix count must never be scaled.
 */
const memo = new Map<string, Dimension>();

export function dimensionOf(field: string): Dimension {
  const hit = memo.get(field);
  if (hit !== undefined) return hit;
  const dim = classifyDimension(field);
  memo.set(field, dim);
  return dim;
}

/**
 * Memoised above, because this is asked per property per row on every bake and
 * again per cell written — and the answer depends on nothing but the name.
 */
function classifyDimension(field: string): Dimension {
  const key = field.toLowerCase();
  if (key.startsWith('dwelling')) return 'none';
  if (AREA_FIELDS.has(key)) return 'area';
  if (LENGTH_FIELDS.has(key)) return 'length';

  const tokens = tokenizeField(field);
  // A rate is never an area: `costPSF` names its unit, and reading it as an
  // area would scale a price by 10.76.
  if (tokens.some((t) => t === 'psf' || t === 'psm')) return 'none';
  if (tokens.includes('area') || tokens.some((t) => AREA_TOKENS.has(t))) return 'area';
  if (tokens.some((t) => LENGTH_TOKENS.has(t))) return 'length';
  return 'none';
}

/** The unit a dimension wears in each system. */
const UNITS: Record<UnitSystem, Record<Exclude<Dimension, 'none'>, string>> = {
  metric: { area: 'm²', length: 'm' },
  imperial: { area: 'ft²', length: 'ft' },
};

/** How many display units to one metric unit. */
function factor(dim: Dimension, system: UnitSystem): number {
  if (system === 'metric') return 1;
  switch (dim) {
    case 'area':
      return FT2_PER_M2;
    case 'length':
      return FT_PER_M;
    default:
      return 1;
  }
}

/** Metric (what the project holds) -> display (what the cell shows). */
export function toDisplay(value: number, dim: Dimension, system: UnitSystem): number {
  return value * factor(dim, system);
}

/** Display (what someone typed) -> metric (what the project must store). */
export function toProject(value: number, dim: Dimension, system: UnitSystem): number {
  return value / factor(dim, system);
}

/** Between two display systems — for values already sitting on a sheet. */
export function reunit(value: number, dim: Dimension, from: UnitSystem, to: UnitSystem): number {
  return toDisplay(toProject(value, dim, from), dim, to);
}

/** A typed cell on its way to the project. Anything but a number passes through. */
export function toProjectValue(field: string, value: CellValue, system: UnitSystem): CellValue {
  const dim = dimensionOf(field);
  if (dim === 'none' || system === 'metric' || typeof value !== 'number' || !Number.isFinite(value)) {
    return value;
  }
  return toProject(value, dim, system);
}

/** Every value in a bag of fields, from one system to another. */
export function reunitFields(
  fields: Record<string, unknown>,
  from: UnitSystem,
  to: UnitSystem
): Record<string, unknown> {
  if (from === to) return fields;
  const out: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(fields)) {
    const dim = dimensionOf(field);
    out[field] =
      dim !== 'none' && typeof value === 'number' && Number.isFinite(value)
        ? reunit(value, dim, from, to)
        : value;
  }
  return out;
}

/** " (ft²)" — empty for a field with no dimension, which is the honest answer. */
export function unitSuffix(field: string, system: UnitSystem): string {
  const dim = dimensionOf(field);
  return dim === 'none' ? '' : ` (${UNITS[system][dim]})`;
}

/**
 * The header a column wears: the pretty field name, then its unit.
 *
 * The unit is shown in BOTH systems. "Gross Area" alone is ambiguous even in a
 * metric project, and spelling it out is what makes switching systems a change
 * of one parenthetical rather than a silent change of every number.
 */
export function columnHeader(field: string, system: UnitSystem): string {
  return `${prettyField(field)}${unitSuffix(field, system)}`;
}

/**
 * Every spelling a header for this field may already carry, lower-cased.
 *
 * Columns are matched to fields BY HEADER (entityTable.ts), so the unit-bearing
 * spellings of both systems have to match too — otherwise switching to feet
 * would leave every dimensioned column orphaned, and the sheet would grow a
 * second copy of it at the right-hand end.
 */
export function headerAliases(field: string): string[] {
  const pretty = prettyField(field);
  const out = [field.toLowerCase(), pretty.toLowerCase()];
  for (const system of ['metric', 'imperial'] as UnitSystem[]) {
    const suffix = unitSuffix(field, system);
    if (suffix !== '') out.push(`${pretty}${suffix}`.toLowerCase(), `${field}${suffix}`.toLowerCase());
  }
  return out;
}

/** The system a provider's numbers are in. Absent means nothing was converted. */
export function unitsOf(data: GiraffeData): UnitSystem {
  return data.units?.() ?? 'metric';
}

function convertRow(row: DataRow, system: UnitSystem): DataRow {
  let out: Record<string, unknown> | null = null;
  for (const [field, value] of Object.entries(row.properties)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const dim = dimensionOf(field);
    if (dim === 'none') continue;
    out ??= { ...row.properties };
    out[field] = toDisplay(value, dim, system);
  }
  // Identity is preserved when nothing needed converting — every cache
  // downstream keys off the row array it was handed.
  return out ? { ...row, properties: out } : row;
}

/**
 * Wrap a provider so every number it hands out is in `system`, and it says so.
 *
 * The provider is the right seam because it is the ONE place the app reads
 * project numbers: the GI.* functions, the entity sheets, the filters and the
 * charts all come through here, so they all speak the same units without any of
 * them knowing about units. `GI.TOTAL("grossArea")` needs no unit code at all,
 * and a filter typed as `grossArea>10000` means what the person reading the
 * sheet thinks it means.
 *
 * Metric returns the base provider untouched — no wrapper, no copies, and the
 * behaviour of every existing workbook is bit-for-bit what it was.
 */
export function withDisplayUnits(base: GiraffeData, system: UnitSystem): GiraffeData {
  if (system === 'metric') return base;

  const cache = new Map<SourceKind, { from: readonly DataRow[]; to: DataRow[] }>();
  const rows = (source: SourceKind): readonly DataRow[] => {
    const from = base.rows(source);
    const hit = cache.get(source);
    if (hit && hit.from === from) return hit.to;
    const to = from.map((row) => convertRow(row, system));
    cache.set(source, { from, to });
    return to;
  };

  return {
    get version() {
      return base.version;
    },
    get live() {
      return base.live;
    },
    units: () => system,
    rows,
    // Block definitions carry columns someone invented, so this app has no
    // basis for a dimension on them — they are shown raw and pushed raw, and
    // the Blocks sheet is the one table with no units in its headers.
    blocks: () => base.blocks(),
    fields: (source) => base.fields(source),
    classOf: (source, field) => base.classOf(source, field),
    inferredClassOf: (source, field) => base.inferredClassOf?.(source, field) ?? base.classOf(source, field),
    selectionKeys: () => base.selectionKeys(),
    siteArea: () => {
      const site = base.siteArea();
      return site === null ? null : toDisplay(site, 'area', system);
    },
    geometryFor: (id) => base.geometryFor?.(id),
    drawingLayers: () => base.drawingLayers?.() ?? [],
    visibleDrawingLayers: () => base.visibleDrawingLayers?.() ?? [],
  };
}
